import { calcCurrency } from '@/lib/use-price';
import {
  Coupon,
  ICartProduct,
  ICreditCard,
  IOrderInput,
  IOrderOutput,
  ISearchProduct,
  TOrderItems,
} from '@/types';
import {
  couponItemEligibilityHasRestrictions,
  isCouponItemEligible,
} from '@/lib/utils/coupon-item-eligibility';
import { findPriceChannelEntry, getPromotionalPack } from '@/lib/utils/product';
import { buildSavedCardPayload } from '@/lib/saved-card-payload';
import { cloneDeep } from 'lodash';
import Decimal from 'decimal.js';

const DEFAULT_PRICE_CHANNEL_ID = 1;
const KIT_PRICE_CHANNEL_ID = 14;

export interface UpdateItemInput extends Partial<Omit<ICartProduct, 'id'>> {}

export function addItemWithQuantity(
  items: ICartProduct[],
  item: ICartProduct,
  quantity: number,
) {
  if (quantity <= 0)
    throw new Error("cartQuantity can't be zero or less than zero");

  const existingItem = items.some((v) => v.id === item.id);

  if (existingItem) {
    const updatedItems = items.map((existingItemValue) => {
      if (existingItemValue.id === item.id) {
        return {
          ...existingItemValue,
          quantity: existingItemValue.quantity! + quantity,
          from_prescription:
            Boolean(existingItemValue.from_prescription) ||
            Boolean(item.from_prescription),
        };
      }
      return existingItemValue;
    });
    return updatedItems;
  }
  return [...items, { ...item, quantity }];
}

export function removeItemOrQuantity(
  items: ICartProduct[],
  id: ICartProduct['id'],
  quantity: number,
) {
  return items.reduce((acc: ICartProduct[], item) => {
    if (item.id === id) {
      const newQuantity = item.quantity! - quantity;

      return newQuantity > 0
        ? [...acc, { ...item, quantity: newQuantity }]
        : [...acc];
    }
    return [...acc, item];
  }, []);
}
// Simple CRUD for Item
export function addItem(items: ICartProduct[], item: ICartProduct) {
  return [...items, item];
}

export function getItem(items: ICartProduct[], id: ICartProduct['id']) {
  return items.find((item) => item.id === id);
}

export function updateItem(
  items: ICartProduct[],
  id: ICartProduct['id'],
  item: UpdateItemInput,
) {
  return items.map((existingItem) =>
    existingItem.id === id ? { ...existingItem, ...item } : existingItem,
  );
}

export function removeItem(items: ICartProduct[], id: ICartProduct['id']) {
  return items.filter((existingItem) => existingItem.id !== id);
}
export function inStock(items: ICartProduct[], id: ICartProduct['id']) {
  const item = getItem(items, id);
  if (item) return item['quantity']! < item['stock']!;
  return false;
}
export const calculateItemTotals = (items: ICartProduct[], settings?: any) => {
  return items.map((item) => {
    // Check if promotional pack pricing should be applied
    const promotionalPackPrice = settings 
      ? getPromotionalPackOrDefaultPrice({ item, settings })
      : { promotional_pack_applied: false, value: item.sale_price };
    
    const effectivePrice = promotionalPackPrice.promotional_pack_applied
      ? item.promotional_pack_unit_price!
      : item.sale_price;

    return {
      ...item,
      price: item.price,
      sale_price: item.sale_price,
      itemTotal: (item.pbm_info?.flag ? item.sale_price : effectivePrice) * item.quantity!,
    };
  });
};

export const getPromotionalPackOrDefaultPrice = ({
  item,
  settings,
}: {
  item?: {
    is_promotional_pack?: boolean;
    promotional_pack_unit_price?: number;
    promotional_pack_quantity?: number; // valueJson.buy
    sale_price?: number;
    quantity?: number;
  };
  settings: any;
}): {
  formatted: string;
  value: number;
  promotional_pack_applied: boolean;
} => {
  const { price: formattedSalePrice } = calcCurrency({
    settings,
    amount: (item?.sale_price || 0) * (item?.quantity || 0),
  });

  const { price: formattedKiPrice } = calcCurrency({
    settings,
    amount: (item?.promotional_pack_unit_price || 0) * (item?.quantity || 0),
  });

  if (
    item?.promotional_pack_unit_price &&
    item?.promotional_pack_quantity &&
    item?.quantity &&
    item?.quantity % item.promotional_pack_quantity === 0
  )
    return {
      formatted: formattedKiPrice,
      value: item.promotional_pack_unit_price,
      promotional_pack_applied: true,
    };

  return {
    formatted: formattedSalePrice,
    value: item?.sale_price || 0,
    promotional_pack_applied: false,
  };
};

export const calculateTotal = (items: ICartProduct[], settings: any) => {
  return items.reduce((total, item) => {
    if (item.pbm_info?.flag) {
      return total + item.sale_price * item.quantity!;
    }
    const itemPrice = getPromotionalPackOrDefaultPrice({
      settings,
      item,
    });
    return total + item.quantity! * itemPrice.value;
  }, 0);
};

export const calculateTotalItems = (items: ICartProduct[]) =>
  items.reduce((sum, item) => sum + item.quantity!, 0);

export const calculateUniqueItems = (items: ICartProduct[]) => items.length;

interface PriceValues {
  totalAmount: number;
  tax: number;
  shipping_charge: number;
}
export const calculatePaidTotal = (
  { totalAmount, tax, shipping_charge }: PriceValues,
  discount?: number,
) => {
  let paidTotal = totalAmount + tax + shipping_charge;
  if (discount) {
    paidTotal = paidTotal - discount;
  }
  return paidTotal;
};

export function formatNumber(
  value: number,
  fractionDigits: number = 2,
): number {
  const factor = Math.pow(10, fractionDigits);
  return Math.round(value * factor) / factor;
}

export const getPromotionalPackDetails = (
  product: ISearchProduct | ICartProduct,
) => {
  const productClone = cloneDeep(product);
  const promotionalPack = getPromotionalPack(productClone?.characteristics);
  const promotional_pack_quantity = promotionalPack?.config.buy;

  const promotional_pack_price_channel =
    productClone?.product_prices?.find((item) => item?.price_channel_id === 14)
      ?.price || 0;

  const productWithPromotionalPackInfo = {
    ...productClone,
    promotional_pack_unit_price: promotional_pack_price_channel,
    promotional_pack_quantity,
    is_promotional_pack: !!promotionalPack,
    apply_promotional_pack: !!(
      promotional_pack_quantity &&
      productClone.quantity! &&
      productClone.quantity! % promotional_pack_quantity === 0
    ),
  } as unknown as ISearchProduct & {
    is_promotional_pack?: boolean;
    promotional_pack_unit_price?: number;
    promotional_pack_quantity?: number;
    apply_promotional_pack?: boolean;
  };

  return {
    is_promotional_pack: !!promotionalPack,
    apply_promotional_pack:
      productWithPromotionalPackInfo.apply_promotional_pack,
    promotional_pack_unit_price: promotional_pack_price_channel,
    promotional_pack_label: promotionalPack
      ? promotionalPack.label ?? 'Leve 3 pague 2'
      : undefined,
    promotional_pack_quantity,
    product: productWithPromotionalPackInfo,
  };
};

export const calcAmount = (items: ISearchProduct[] | ICartProduct[]) =>
  formatNumber(
    items.reduce(
      (acc, item) => item.sale_price! * (item.quantity || 1) + acc,
      0,
    ),
  );

export const applyPromotionalPackToOrder = (
  orderEntity: IOrderInput,
): IOrderInput => {
  const orderEntityCopy = cloneDeep(orderEntity);
  const order_items = cloneDeep(orderEntity.order_items);

  const promotional_pack_order_items = cloneDeep(order_items).map((item) => {
    const promotional_pack_details = getPromotionalPackDetails(item);

    if (
      item.is_promotional_pack &&
      promotional_pack_details.product.apply_promotional_pack
    ) {
      item.sale_price = item.promotional_pack_unit_price!;
      item.calculated_sale_price = item.promotional_pack_unit_price!;
      item.subtotal = item.promotional_pack_unit_price! * item.quantity!;
    }

    return item;
  });

  orderEntityCopy.order_items = promotional_pack_order_items;

  return orderEntityCopy;
};

/** Sum of merchandise subtotals that count toward coupon discount (matches API eligible subtotal). */
export const getCouponEligibleMerchandiseSubtotal = (
  items: ICartProduct[],
  coupon: Pick<Coupon, 'properties'> | null | undefined,
  settings: any,
): number => {
  if (
    !coupon?.properties ||
    !couponItemEligibilityHasRestrictions(coupon.properties)
  ) {
    return calculateTotal(items, settings);
  }
  return items.reduce((sum, item) => {
    const unitPrice = item.pbm_info?.flag
      ? item.sale_price!
      : getPromotionalPackOrDefaultPrice({ settings, item }).value;
    if (
      !isCouponItemEligible(
        {
          sku: item.sku,
          order_quantity: item.quantity,
          original_unit_price: unitPrice,
          category_ids: item.category_ids ?? [],
        },
        coupon.properties,
      )
    ) {
      return sum;
    }
    if (item.pbm_info?.flag) {
      return sum + item.sale_price! * item.quantity!;
    }
    const itemPrice = getPromotionalPackOrDefaultPrice({ settings, item });
    return sum + item.quantity! * itemPrice.value;
  }, 0);
};

export const applyCouponToOrder = (
  orderEntity: IOrderInput,
  coupon?: Coupon | null,
) => {
  if (!coupon) return orderEntity;

  const restricted = couponItemEligibilityHasRestrictions(coupon.properties);

  const lineMeta = orderEntity.order_items.map((item) => {
    const total = new Decimal(item.calculated_sale_price!).mul(item.quantity!);
    const eligible = isCouponItemEligible(
      {
        sku: item.sku,
        order_quantity: item.quantity,
        original_unit_price: item.calculated_sale_price ?? item.sale_price,
        category_ids: item.category_ids ?? [],
      },
      coupon.properties,
    );
    return { item, total, eligible };
  });

  const eligibleSubtotalDecimal = lineMeta
    .filter((x) => x.eligible)
    .reduce((acc, x) => acc.plus(x.total), new Decimal(0));

  const discountBasisDecimal = restricted
    ? eligibleSubtotalDecimal
    : new Decimal(orderEntity.amount);

  switch (coupon.type) {
    case 'fixed': {
      const rawDiscount = Decimal.min(
        new Decimal(coupon.amount),
        discountBasisDecimal,
      );
      orderEntity.discount = formatNumber(rawDiscount.toNumber());
      break;
    }
    case 'percentage':
      orderEntity.discount = formatNumber(
        discountBasisDecimal.mul(coupon.amount).div(100).toNumber(),
      );
      break;
    case 'free_shipping':
      orderEntity.delivery_fee = 0;
      break;
  }

  lineMeta.forEach(({ item, total, eligible }) => {
    const order_quantity = new Decimal(item.quantity!);

    switch (coupon.type) {
      case 'fixed': {
        if (!eligible) {
          item.calculated_sale_price = formatNumber(item.calculated_sale_price!);
          item.subtotal = formatNumber(total.toNumber());
          item.discount = 0;
          break;
        }
        const denom = restricted
          ? eligibleSubtotalDecimal
          : new Decimal(orderEntity.amount);
        if (denom.isZero()) {
          item.subtotal = formatNumber(total.toNumber());
          item.discount = 0;
          break;
        }
        const fixedPercentage = total.div(denom);
        const fixedDiscount = fixedPercentage.mul(orderEntity.discount ?? 0);
        const fixedCalculation = total.sub(fixedDiscount);
        item.calculated_sale_price = formatNumber(
          fixedCalculation.div(order_quantity).toNumber(),
        );
        item.subtotal = formatNumber(fixedCalculation.toNumber());
        item.discount = formatNumber(fixedDiscount.toNumber());
        break;
      }
      case 'percentage': {
        if (!eligible) {
          item.calculated_sale_price = formatNumber(item.calculated_sale_price!);
          item.subtotal = formatNumber(total.toNumber());
          item.discount = 0;
          break;
        }
        const percentageDiscount = new Decimal(item.calculated_sale_price!)
          .mul(coupon.amount)
          .div(100);
        const percentageCalculation = new Decimal(
          item.calculated_sale_price!,
        ).sub(percentageDiscount);
        item.calculated_sale_price = formatNumber(
          percentageCalculation.toNumber(),
        );
        item.subtotal = formatNumber(
          new Decimal(item.calculated_sale_price)
            .mul(item.quantity!)
            .toNumber(),
        );
        item.discount = formatNumber(percentageDiscount.toNumber());
        break;
      }
      case 'free_shipping':
        item.subtotal = formatNumber(
          new Decimal(item.calculated_sale_price!)
            .mul(item.quantity!)
            .toNumber(),
        );
        item.discount = 0;
        break;
    }
  });

  return orderEntity;
};

/** Catalog price aligned with API OrderValidator (DEFAULT channel 1, KIT channel 14). */
export function resolveOrderOriginalUnitPrice(item: ICartProduct): number {
  // PBM lines must match buildPBMVerifyPricesPayload / API PbmApplier validation.
  if (item.pbm_info?.flag) {
    const pbmUnit =
      typeof item.sale_price === 'number' && Number.isFinite(item.sale_price)
          ? item.sale_price
        : typeof item.calculated_sale_price === 'number' &&
            Number.isFinite(item.calculated_sale_price)
          ? item.calculated_sale_price
          : 0;
    return formatNumber(pbmUnit);
  }

  const kitChannel = findPriceChannelEntry(
    item.product_prices ?? [],
    KIT_PRICE_CHANNEL_ID,
  );
  const defaultChannel = findPriceChannelEntry(
    item.product_prices ?? [],
    DEFAULT_PRICE_CHANNEL_ID,
  );

  const kitApplies =
    item.is_promotional_pack &&
    item.promotional_pack_quantity &&
    item.quantity &&
    item.quantity % item.promotional_pack_quantity === 0;

  if (kitApplies && kitChannel) {
    const kitValue = Number(kitChannel.sale_price ?? kitChannel.price);
    if (Number.isFinite(kitValue) && kitValue > 0) {
      return formatNumber(kitValue);
    }
  }

  if (defaultChannel) {
    const defaultValue = Number(defaultChannel.sale_price ?? defaultChannel.price);
    if (Number.isFinite(defaultValue) && defaultValue > 0) {
      return formatNumber(defaultValue);
    }
  }

  return formatNumber(item.sale_price ?? 0);
};

export const formatOutput = (orderEntity: IOrderInput): IOrderOutput => {
  // Format all monetary values to 2 decimal places
  const formattedEntity = {
    ...orderEntity,
    amount: formatNumber(calcAmount(orderEntity.order_items)), // sum of prices without shipping and discount
    total: formatNumber(orderEntity.total ?? 0),
    paid_total: formatNumber(orderEntity.paid_total ?? 0),
    discount: formatNumber(orderEntity.discount ?? 0),
    delivery_fee: formatNumber(orderEntity.delivery_fee ?? 0),
  };

  return {
    ...formattedEntity,
    order_items: orderEntity.order_items.map((item) => ({
      order_quantity: item.quantity,
      original_unit_price: resolveOrderOriginalUnitPrice(item),
      // TODO: review and improve way of format prices before send
      unit_price: formatNumber(item.calculated_sale_price ?? 0),
      subtotal: formatNumber(
        (item.calculated_sale_price ?? 0) * (item.quantity ?? 1),
      ),
      // paid_total: paid_total! * item.quantity!,
      product_id: item.product_id,
      sku: item.sku,
      ...(item.from_prescription
        ? { custom_properties: { from_prescription: true } }
        : {}),
    })) as TOrderItems[],
  };
};

export const applyPaymentInfo = ({
  orderEntity,
  payment_gateway,
  credit_card,
  installments = 1,
}: {
  orderEntity: IOrderInput;
  payment_gateway: number;
  credit_card?: ICreditCard;
  installments?: number;
}): IOrderInput => {
  const orderEntityCopy = cloneDeep(orderEntity) as IOrderInput & {
    payment_gateway_ids?: number[];
  };
  
  if (orderEntityCopy.payment_gateway_ids && orderEntityCopy.payment_gateway_ids.length > 0) {
    orderEntityCopy.payment_gateway_ids[0] = payment_gateway;
  } else {
    orderEntityCopy.payment_gateway_ids = [payment_gateway];
  }

  if (payment_gateway === 5) {
    orderEntityCopy.credit_card = credit_card?.token
      ? buildSavedCardPayload(credit_card, installments)
      : {
          card_number: credit_card?.card_number,
          cvv: credit_card?.cvv,
          expiration_month: credit_card?.expiration_month,
          expiration_year: credit_card?.expiration_year,
          holder_name: credit_card?.holder_name,
          save_card: credit_card?.save_card,
          installments,
        };
  }

  return orderEntityCopy;
};
